import { createContext, useReducer, Dispatch, ReactNode, useContext, useMemo } from 'react';
import IFilterValue from '../models/iFilterValue';
import IAttributeDto from '../models/iAttributeDto';
import ISalesOrg from '../models/iSalesOrg';
import ProductTypes from '../enum/ProductTypesEnum';
import { EnabledProductTypes, defaultEnabledProductTypes } from '../types/ProductTypeFilter';

interface SearchState {
  filters: IFilterValue[];
  attributes: IAttributeDto[];
  enabledProductTypes: EnabledProductTypes;
  selectedIndexes: string[];
  allIndexes: string[];
  selectedSalesOrgs: ISalesOrg[];
  allSalesOrgs: ISalesOrg[];
  facetRefreshTrigger: number;
  searchResetTrigger: number;
}

type SearchAction =
  | { type: 'UPDATE_FILTER'; payload: IFilterValue }
  | { type: 'REMOVE_FILTER'; payload: { id: number } }
  | { type: 'ADD_FILTER' }
  | { type: 'SET_FILTERS'; payload: { filters: IFilterValue[]; allSalesOrgs?: ISalesOrg[] } }
  | { type: 'RESET_FILTERS'; payload: { initialFilters: IFilterValue[] } }
  | { type: 'SYNC_FILTERS_WITH_ATTRIBUTES'; payload: { attributes: IAttributeDto[]; indexNames: string[] } }
  | { type: 'TOGGLE_PRODUCT_TYPE'; payload: ProductTypes }
  | { type: 'SET_SELECTED_INDEXES'; payload: string[] }
  | { type: 'SET_SELECTED_SALES_ORGS'; payload: ISalesOrg[] }
  | { type: 'SET_AVAILABLE_SALES_ORGS'; payload: ISalesOrg[] }
  | { type: 'SET_AVAILABLE_INDEXES'; payload: string[] };

const initialFilters: IFilterValue[] = [];
const initialState: SearchState = {
  filters: initialFilters,
  attributes: [],
  enabledProductTypes: defaultEnabledProductTypes,
  selectedIndexes: [],
  allIndexes: [],
  selectedSalesOrgs: [],
  allSalesOrgs: [],
  facetRefreshTrigger: 0,
  searchResetTrigger: 0,
};

const filterReducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case 'UPDATE_FILTER':
      return {
        ...state,
        filters: state.filters.map((f) => (f.id === action.payload.id ? action.payload : f)),
      };

    case 'REMOVE_FILTER':
      // Ensure we don't remove the last filter if it's required
      if (state.filters.length <= 1) return state;
      return {
        ...state,
        filters: state.filters.filter((f) => f.id !== action.payload.id),
      };

    case 'ADD_FILTER': {
      const hasIncompleteFilter = state.filters.some((f) => !f.selectedValue && !f.lowerBound && !f.upperBound);
      if (hasIncompleteFilter || state.filters.length >= state.attributes.length) {
        return state; // Don't add if an empty one exists or we've hit the limit
      }
      return { ...state, filters: [...state.filters, { id: Date.now() }] };
    }

    case 'SET_FILTERS':
      return {
        ...state,
        filters: action.payload.filters,
      };

    case 'RESET_FILTERS':
      return {
        ...initialState,
        filters: action.payload.initialFilters,
        enabledProductTypes: defaultEnabledProductTypes,
        selectedIndexes: [], // Empty = search all
        allIndexes: state.allIndexes,
        selectedSalesOrgs: [], // Empty = search all
        allSalesOrgs: state.allSalesOrgs,
        searchResetTrigger: Date.now(),
        facetRefreshTrigger: Date.now(),
      };

    case 'SYNC_FILTERS_WITH_ATTRIBUTES': {
      const { attributes, indexNames } = action.payload;
      const availableNames = new Set(attributes.map((attr) => attr.attributeName));

      const multiIndexFilterComparator = (a: IFilterValue, b: IFilterValue): number => {
        const aName = a.attribute?.attributeName;
        const bName = b.attribute?.attributeName;

        // Rule 1: 'Keyword' always comes first.
        if (aName === 'Keyword') return -1;
        if (bName === 'Keyword') return 1;

        // Rule 2: 'Catalog' always comes last.
        if (aName === 'Catalog') return 1;
        if (bName === 'Catalog') return -1;

        return 0;
      };

      const singleIndexAttributeComparator = (a: IAttributeDto, b: IAttributeDto): number => {
        const aName = a.attributeName;
        const bName = b.attributeName;

        // Rule 1: 'Catalog' is always last.
        if (aName === 'Catalog') return 1;
        if (bName === 'Catalog') return -1;

        // Rule 2: 'Keyword' is second to last.
        if (aName === 'Keyword') return 1;
        if (bName === 'Keyword') return -1;

        // Rule 3: 'Material' is always first.
        if (aName === 'Material') return -1;
        if (bName === 'Material') return 1;

        // Rule 4: Sort everything else alphabetically.
        return aName.localeCompare(bName);
      };

      let preservedFilters = state.filters.filter((f) => {
        const hasValue = f.selectedValue || f.lowerBound != null || f.upperBound != null;
        const attributeIsValid = f.attribute && availableNames.has(f.attribute.attributeName);
        return hasValue && attributeIsValid;
      });

      if (indexNames.length > 1 || indexNames.length === 0) {
        // Logic for multiple indexes OR empty = all
        const targetCount = attributes.length;
        while (preservedFilters.length < targetCount) {
          preservedFilters.push({ id: Date.now() + preservedFilters.length });
        }
        const usedNames = new Set(preservedFilters.map((f) => f.attribute?.attributeName).filter(Boolean));
        const availableAttrs = attributes.filter((attr) => !usedNames.has(attr.attributeName));
        preservedFilters = preservedFilters.map((filter) => {
          if (!filter.attribute && availableAttrs.length > 0) {
            return { ...filter, attribute: availableAttrs.shift() };
          }
          return filter;
        });

        preservedFilters.sort(multiIndexFilterComparator);
      } else if (indexNames.length === 1) {
        // Logic for a single index
        attributes.sort(singleIndexAttributeComparator);
        const targetCount = Math.min(3, attributes.length);
        while (preservedFilters.length < targetCount) {
          preservedFilters.push({ id: Date.now() + preservedFilters.length });
        }
      }

      return { ...state, attributes, filters: preservedFilters.length > 0 ? preservedFilters : initialFilters };
    }

    case 'TOGGLE_PRODUCT_TYPE': {
      const newProductTypes = state.enabledProductTypes.includes(action.payload)
        ? state.enabledProductTypes.filter((t) => t !== action.payload)
        : [...state.enabledProductTypes, action.payload];
      return { ...state, enabledProductTypes: newProductTypes, facetRefreshTrigger: Date.now() };
    }

    case 'SET_SELECTED_INDEXES':
      return { ...state, selectedIndexes: action.payload, facetRefreshTrigger: Date.now() };

    case 'SET_SELECTED_SALES_ORGS':
      return { ...state, selectedSalesOrgs: action.payload, facetRefreshTrigger: Date.now() };

    case 'SET_AVAILABLE_SALES_ORGS':
      return { ...state, allSalesOrgs: action.payload };

    case 'SET_AVAILABLE_INDEXES':
      return { ...state, allIndexes: action.payload };

    default:
      return state;
  }
};

const SearchContext = createContext<{
  state: SearchState;
  dispatch: Dispatch<SearchAction>;
  initialFilters: IFilterValue[];
  getSalesOrgName: (salesOrgId: number) => string;
  salesOrgMap: Map<number, string>;
}>({
  state: initialState,
  dispatch: () => null,
  initialFilters,
  getSalesOrgName: () => '',
  salesOrgMap: new Map(),
});

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(filterReducer, initialState);

  const salesOrgMap = useMemo(() => {
    return new Map(state.allSalesOrgs.map((org) => [org.salesOrgId, org.name]));
  }, [state.allSalesOrgs]);

  const getSalesOrgName = useMemo(() => {
    return (salesOrgId: number): string => {
      return salesOrgMap.get(salesOrgId) || salesOrgId.toString();
    };
  }, [salesOrgMap]);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      initialFilters,
      getSalesOrgName,
      salesOrgMap,
    }),
    [state, getSalesOrgName, salesOrgMap]
  );

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>;
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
